/**
 * H1 HPD housing violations (server `tracking/services/hpd_violation_records.py`,
 * GET portal/buildings/<id>/hpd-violations/). No violation is priced: Admin
 * Code 27-2115 amounts are unverified (Leo, 2026-10-02). `charges` is HPD's
 * own billed line, shown as billed and never summed.
 *
 * TRAP: humps camelizes single-letter keys, so `open_by_class: {C: 17}` arrives
 * as `openByClass: {c: 17}`. Read it through `openOfClass`.
 */
import { useQuery } from "@tanstack/react-query";

import api from "@/api/base";
import { formatDay, nyClock } from "@/pages/Portal/ActionPlan/actionPlan";
import { notFetchedLabel } from "./exposureAxes";
import { day, type HpdCharges } from "./HpdChargesSection";
import { httpStatusOf } from "./httpStatus";
import { usePortalFeatures } from "./usePortalFeatures";

export interface HpdFlag {
  key: string;
  label: string;
}

export interface HpdViolationRow {
  violationId: string;
  hpdBuildingId: string;
  bin: string;
  address: string;
  class: string;
  status: string;
  statusDate: string | null;
  open: boolean;
  novId: string | null;
  novIssued: string | null;
  correctBy: string | null;
  certifyBy: string | null;
  /** T-1 while ahead, HPD's own named once in the note; then HPD's date. Past counts from HPD's date (Q2,
   *  Leo 10-15). Absent before 2026-10-11. */
  certifyByShown?: string | null;
  certifyByNote?: string | null;
  certifiedOn: string | null;
  apartment: string | null;
  story: string | null;
  description: string;
  rentImpairing: boolean;
  flags: HpdFlag[];
  /** Due, and never lead, mold or pests: eCertification takes it. Absent before 2026-10-12. */
  ecertEligible?: boolean;
  /** `opendata` (wvxf-dwi5 row) is staff only; the view strips it (D3). */
  links: { hpdOnline: string | null; opendata?: string };
}

export interface HpdSection {
  hpdBuildingId: string;
  bin: string;
  address: string;
  /** The writer could not tell which HPD building on this BIN is ours. */
  sharedBin: boolean;
  hpdOnline: string | null;
}

export interface HpdCounts {
  openTotal: number;
  openByClass: Record<string, number>;
  closed12m: number;
  /** Past certify-by and not certified on time (late certifications included). */
  pastCertifyBy: number;
  /** Past certify-by, certified, HPD refused it. Absent on a pre-2026-10-03 cache. */
  certificationRefused?: number;
  /** Past certify-by, certified on time; HPD has not closed it. */
  certifiedAwaitingHpd: number;
  /** DEFECT LETTER ISSUED: HPD saw it fixed, papers owed, never past. Absent before 2026-10-10. */
  papersOwed?: number;
}

export interface HpdViolationsPayload {
  source: string;
  fetch: { state: "success" | "failure" | "unknown"; asOf: string | null };
  asOf: string | null;
  /** HPD Online read every HPD id here; status as of then. Absent before 2026-10-25. */
  statusAsOf?: string | null;
  closedSince: string;
  binsRead: string[];
  sections: HpdSection[];
  counts: HpdCounts | null;
  open: HpdViolationRow[];
  closed12m: HpdViolationRow[];
  /** The server's New York date; every window here counts from it. */
  today: string;
  /** Open Class C due today..today+30, the H2 lane's and H3's one list. */
  classCDue30d?: {
    violationId: string;
    certifyBy: string;
    certifyByShown?: string | null;
    certifyByNote?: string | null;
  }[];
  /** `hpd_charges_line`, the Signals pane's own line. Never summed. */
  charges?: HpdCharges | null;
  /** Charges switch, HPD Online or its head off: work orders unread. */
  chargesUnread?: boolean;
}

export const hpdViolationsQueryKey = (buildingId: string) =>
  ["portal", "building", buildingId, "hpd-violations"] as const;

/** 404 = flag off (body `reason: "module_off"`) or not this tenant's building.
 * Switch portal_hpd_exposure_lane off: no fetch, and the key's `on` suffix
 * drops cached rows, so no surface reads HPD as clean (Leo, 2026-10-05). */
export const useBuildingHpdViolationsQuery = (buildingId: string) => {
  const on = usePortalFeatures().hpdExposureLane;
  return useQuery<HpdViolationsPayload>({
    queryKey: [...hpdViolationsQueryKey(buildingId), on],
    enabled: on && buildingId.length > 0,
    queryFn: async () =>
      (await api.get(`tracking/portal/buildings/${buildingId}/hpd-violations/`))
        .data,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
};

/** Only the named flag-off 404 hides the section; a bare 404 (route missing,
 * wrong building) is an error. */
export const isHpdOff = (error: unknown): boolean =>
  httpStatusOf(error) === 404 &&
  (error as { response?: { data?: { reason?: unknown } } }).response?.data
    ?.reason === "module_off";

/** Refresh cannot fix a bare 404, so only 5xx and network errors offer it. */
export const hpdErrorLine = (error: unknown): string =>
  httpStatusOf(error) === 404
    ? "HPD violations are not available for this building."
    : "HPD violations could not be loaded. Refresh to try again.";

/** HPD's own class names (hpd.nyc.gov, "Violation classes"). */
export const HPD_CLASSES = [
  { cls: "C", meaning: "Immediately hazardous" },
  { cls: "B", meaning: "Hazardous" },
  { cls: "A", meaning: "Non-hazardous" },
  { cls: "I", meaning: "Information order" },
] as const;

/** Enrollment is not public, so "if enrolled" (`hpd_violation_records.ecert_eligible`). */
export const ECERT_LINE =
  "Certify online in HPD eCertification, if the building is enrolled.";

export const HPD_NOV_COPY =
  "HPD mails the NOV to the registered owner. It is not posted online.";

export const openOfClass = (counts: HpdCounts, cls: string): number =>
  counts.openByClass?.[cls.toLowerCase()] ?? 0;

/** "18 open. 8 Class C." Zero Class C drops. */
export const hpdHeaderLine = (counts: HpdCounts): string => {
  if (counts.openTotal === 0) return "No open HPD violations.";
  const c = openOfClass(counts, "C");
  return c > 0
    ? `${counts.openTotal} open. ${c} Class C.`
    : `${counts.openTotal} open.`;
};

/** DEFECT LETTER words: this line, and the Action plan's State column and deck (Leo 10-10). */
export const PAPERS_OWED = "Papers owed";

/** Every part of past certify-by, then papers owed whatever the date; null when
 * none has a row. A zero refused or papers part drops. */
export const hpdCertifyLine = (counts: HpdCounts): string | null => {
  const refused = counts.certificationRefused ?? 0;
  const papers = counts.papersOwed ?? 0;
  if (
    counts.pastCertifyBy + refused + counts.certifiedAwaitingHpd + papers ===
    0
  )
    return null;
  return [
    `${counts.pastCertifyBy} past certify-by, not certified on time.`,
    refused > 0 && `${refused} certified, HPD did not accept the certification.`,
    `${counts.certifiedAwaitingHpd} certified on time, awaiting HPD.`,
    papers > 0 && `HPD saw ${papers} fixed; ${PAPERS_OWED.toLowerCase()}.`,
  ]
    .filter(Boolean)
    .join(" ");
};

/** Rows whose certify-by date passed with no accepted certification. */
export const MISSED_CERTIFY_FLAGS = new Set([
  "past_certify_by",
  "certification_refused",
]);

export interface HpdClassGroup {
  cls: string;
  meaning: string;
  rows: HpdViolationRow[];
}

/** C, B, A, I, then anything HPD adds; the server's row order holds inside. */
export const groupByClass = (
  rows: readonly HpdViolationRow[],
): HpdClassGroup[] => {
  const known: string[] = HPD_CLASSES.map((k) => k.cls);
  const order = [
    ...known,
    ...Array.from(new Set(rows.map((r) => r.class))).filter(
      (c) => !known.includes(c),
    ),
  ];
  return order
    .map((cls) => ({
      cls,
      meaning:
        HPD_CLASSES.find((k) => k.cls === cls)?.meaning ??
        `Class ${cls || "unknown"}`,
      rows: rows.filter((r) => r.class === cls),
    }))
    .filter((g) => g.rows.length > 0);
};

/** HPD's certification findings and missed dates read as urgent; the rest neutral. */
const URGENT_FLAGS = new Set([
  "false_certification",
  "certified_not_complied",
  "certification_rejected",
  ...MISSED_CERTIFY_FLAGS,
]);
const WATCH_FLAGS = new Set(["defect_letter", "reopened", "certified_late"]);

export const flagTone = (key: string): "high" | "med" | "neutral" =>
  URGENT_FLAGS.has(key) ? "high" : WATCH_FLAGS.has(key) ? "med" : "neutral";

/** Null when read; else the line that replaces every count. Never "0 open". */
export const hpdNotFetchedLine = (
  payload: Pick<HpdViolationsPayload, "fetch">,
  buildingName: string,
): string | null => {
  const label = notFetchedLabel({
    enforcementFetch: { state: payload.fetch.state, notFetched: [] },
  });
  return label
    ? `HPD violations for ${buildingName} ${label.toLowerCase()}.`
    : null;
};

/** "74 open. Class C 24, B 37, A 13." Zero classes drop. */
export const hpdClassLine = (counts: HpdCounts): string => {
  if (counts.openTotal === 0) return "No open HPD violations.";
  const parts = HPD_CLASSES.map(
    (k) => [k.cls, openOfClass(counts, k.cls)] as const,
  )
    .filter(([, n]) => n > 0)
    .map(([cls, n]) => `${cls} ${n}`);
  return `${counts.openTotal} open. Class ${parts.join(", ")}.`;
};

/** HPD's findings on a certification, in this order; labels are the server's. */
const LANE_FINDINGS = [
  "false_certification",
  "certified_not_complied",
  "certification_rejected",
] as const;

export interface HpdFinding {
  key: string;
  label: string;
  ids: string[];
}

export const hpdFindings = (open: readonly HpdViolationRow[]): HpdFinding[] =>
  LANE_FINDINGS.flatMap((key) => {
    const rows = open.filter((r) => r.flags.some((f) => f.key === key));
    if (!rows.length) return [];
    const label = rows[0].flags.find((f) => f.key === key)?.label ?? key;
    return [{ key, label, ids: rows.map((r) => r.violationId) }];
  });

/** One read date: HPD Online's when it read every id here ("Status from HPD Online, read Sun Oct 25"). */
export const hpdSourceLine = (p: HpdViolationsPayload): string =>
  p.statusAsOf
    ? `Source: ${p.source}. Status from HPD Online, read ${formatDay(nyClock(p.statusAsOf).day, p.today)}.`
    : `Source: ${p.source}${p.asOf ? `, read ${day(p.asOf)}` : ""}.`;

/**
 * May a page call this building clean ("Nothing owed to the city")? Only when
 * HPD was read with no open row and HPD Online read the work orders
 * (`chargesUnread` false). The flag-off 404 restores the pre-HPD answer. 20-25 Shore, 2026-10-02: $0 enforcement, 2 open Class C.
 */
export const hpdAllowsAllClear = (q: {
  data?: HpdViolationsPayload;
  isError: boolean;
  error: unknown;
}): boolean => {
  if (q.isError) return isHpdOff(q.error);
  const p = q.data;
  return Boolean(
    p?.counts &&
      p.counts.openTotal === 0 &&
      !p.chargesUnread &&
      !p.charges?.workOrders.length,
  );
};

export const exposureEmptyFallback = (hpdClear: boolean): string =>
  hpdClear
    ? "No city enforcement is on record for this building."
    : "No OATH, DOB or ECB money is on record for this building.";
